import type { Expense, ExpensePaidBy } from "./expenseTypes";

/**
 * Almikia Epic EG (TASK-043): pure expense rules shared by both data providers
 * and the screens. They mirror the TASK-042 schema (generated `iva_amount`,
 * CHECK constraints) so a FakeRest demo rejects exactly what the database
 * would. All money is MXN with 2 decimals and is rounded ONLY by
 * {@link roundMoney}.
 */

/** Default IVA rate (16%); mirrors `expenses.iva_rate` default in the database. */
export const DEFAULT_IVA_RATE = 0.16;

/** Case-insensitive 8-4-4-4-12 hex: same shape as the `expenses_cfdi_uuid_check` regex. */
const CFDI_UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export const REIMBURSABLE_PAID_BY: ExpensePaidBy = "pepe_diaz";

/** Spanish messages, one per validation rule (surfaced as-is by the forms). */
export const EXPENSE_MESSAGES = {
  dateRequired: "Indica la fecha del gasto",
  conceptRequired: "Indica el concepto del gasto",
  categoryRequired: "Elige una categoría",
  amountPositive: "El monto debe ser mayor a cero",
  paymentMethodRequired: "Elige la forma de pago",
  paidByRequired: "Indica quién pagó",
  cfdiUuidRequired: "Indica el UUID del CFDI cuando el gasto está facturado",
  cfdiUuidInvalid: "El UUID del CFDI no es válido",
  paidByOtherRequired: "Indica el nombre de quien pagó",
  reimbursedOnlyPepe: "Solo se reembolsan los gastos pagados por Pepe Díaz",
  reimbursedAtRequired: "Indica la fecha del reembolso",
  reimbursedAtFuture: "La fecha del reembolso no puede ser futura",
  cancelReasonRequired: "Indica el motivo de la cancelación",
  cancelled: "Un gasto cancelado no se puede editar",
  alreadyCancelled: "El gasto ya está cancelado",
  notFound: "No encontramos el gasto",
  noDelete: "Los gastos no se eliminan: cancela el gasto con un motivo",
  forbidden: "No tienes permiso para gestionar gastos",
  categoryNameRequired: "Indica el nombre de la categoría",
  categoryDuplicate: "Ya existe una categoría con ese nombre",
} as const;

/**
 * Rounds a MXN amount half up to cents. The single place money is rounded: the
 * small epsilon absorbs binary float noise (e.g. 1.005 stays 1.01).
 */
export function roundMoney(value: number): number {
  const sign = value < 0 ? -1 : 1;
  return (sign * Math.round(Math.abs(value) * 100 + 1e-7)) / 100;
}

/**
 * IVA contained in an IVA-INCLUSIVE amount (ASSUMPTION QEG-A5, same as the
 * generated column): `amount - amount / (1 + rate)` rounded to cents; 0 when
 * the expense has no IVA. The rate is data (default {@link DEFAULT_IVA_RATE}).
 */
export function computeIvaAmount(
  amount: number,
  hasIva: boolean,
  rate: number = DEFAULT_IVA_RATE,
): number {
  if (!hasIva) return 0;
  return roundMoney(amount - amount / (1 + rate));
}

/** Amount before IVA, derived from the same rule. */
export function computeSubtotal(
  amount: number,
  hasIva: boolean,
  rate: number = DEFAULT_IVA_RATE,
): number {
  return roundMoney(amount - computeIvaAmount(amount, hasIva, rate));
}

/** True when `value` has the CFDI UUID shape (8-4-4-4-12 hex, any case). */
export function isValidCfdiUuid(value: string | null | undefined): boolean {
  return typeof value === "string" && CFDI_UUID_PATTERN.test(value.trim());
}

/** Only an uncancelled expense paid by Pepe Diaz can be reimbursed (QEG-A3). */
export function isReimbursable(
  expense: Pick<Expense, "paid_by" | "cancelled_at">,
): boolean {
  return (
    expense.paid_by === REIMBURSABLE_PAID_BY && expense.cancelled_at == null
  );
}

/** The columns a signed-in user may write (mirrors the column grants of TASK-042). */
export const EXPENSE_WRITABLE_COLUMNS = [
  "expense_date",
  "concept",
  "category_id",
  "supplier",
  "amount_mxn",
  "has_iva",
  "iva_rate",
  "is_invoiced",
  "cfdi_uuid",
  "cfdi_pdf_path",
  "cfdi_xml_path",
  "paid_by",
  "paid_by_other",
  "payment_method",
  "project_id",
  "notes",
  "receipt_path",
  "reimbursed",
  "reimbursed_at",
] as const;

export type ExpenseWritableColumn = (typeof EXPENSE_WRITABLE_COLUMNS)[number];
export type ExpenseWriteData = Partial<Pick<Expense, ExpenseWritableColumn>>;

const isBlank = (value: unknown) =>
  value == null || (typeof value === "string" && value.trim() === "");

const blankToNull = (value: unknown) => (isBlank(value) ? null : value);

const trimOrNull = (value: unknown) =>
  typeof value === "string" ? value.trim() || null : (value ?? null);

/**
 * Reduces a form payload to the writable columns and normalises it so the
 * database CHECK constraints hold: blank text becomes null, the UUID is only
 * kept when invoiced, the free-text payer only for `otro`, the reimbursement
 * date only when reimbursed. Anything else (id, iva_amount, created_*,
 * updated_*, cancelled_*, view-only fields) is dropped, or PostgREST rejects the
 * write with 42501.
 */
export function toExpenseWriteData(
  data: Record<string, unknown>,
): ExpenseWriteData {
  const picked: Record<string, unknown> = {};
  for (const column of EXPENSE_WRITABLE_COLUMNS) {
    if (column in data) picked[column] = data[column];
  }
  for (const column of [
    "supplier",
    "notes",
    "cfdi_pdf_path",
    "cfdi_xml_path",
    "receipt_path",
    "project_id",
  ]) {
    if (column in picked) picked[column] = blankToNull(picked[column]);
  }
  // The dependent column is only cleared when its controlling column is part of
  // the payload (a form sends the whole record; a partial patch must not wipe it).
  if ("cfdi_uuid" in picked) {
    picked.cfdi_uuid =
      "is_invoiced" in picked && !picked.is_invoiced
        ? null
        : trimOrNull(picked.cfdi_uuid);
  }
  if ("paid_by_other" in picked) {
    picked.paid_by_other =
      "paid_by" in picked && picked.paid_by !== "otro"
        ? null
        : trimOrNull(picked.paid_by_other);
  }
  if ("reimbursed_at" in picked) {
    picked.reimbursed_at =
      "reimbursed" in picked && !picked.reimbursed
        ? null
        : blankToNull(picked.reimbursed_at);
  }
  return picked as ExpenseWriteData;
}

export type ExpenseErrors = Partial<Record<ExpenseWritableColumn, string>>;

/**
 * Form validation of a complete expense payload. Returns one Spanish message
 * per invalid field; an empty object means valid.
 */
export function validateExpense(data: ExpenseWriteData): ExpenseErrors {
  const errors: ExpenseErrors = {};
  if (isBlank(data.expense_date)) {
    errors.expense_date = EXPENSE_MESSAGES.dateRequired;
  }
  if (isBlank(data.concept)) errors.concept = EXPENSE_MESSAGES.conceptRequired;
  if (isBlank(data.category_id)) {
    errors.category_id = EXPENSE_MESSAGES.categoryRequired;
  }
  if (isBlank(data.payment_method)) {
    errors.payment_method = EXPENSE_MESSAGES.paymentMethodRequired;
  }
  if (isBlank(data.paid_by)) errors.paid_by = EXPENSE_MESSAGES.paidByRequired;
  if (
    typeof data.amount_mxn !== "number" ||
    !Number.isFinite(data.amount_mxn) ||
    data.amount_mxn <= 0
  ) {
    errors.amount_mxn = EXPENSE_MESSAGES.amountPositive;
  }
  if (data.is_invoiced) {
    if (isBlank(data.cfdi_uuid)) {
      errors.cfdi_uuid = EXPENSE_MESSAGES.cfdiUuidRequired;
    } else if (!isValidCfdiUuid(data.cfdi_uuid)) {
      errors.cfdi_uuid = EXPENSE_MESSAGES.cfdiUuidInvalid;
    }
  }
  if (data.paid_by === "otro" && isBlank(data.paid_by_other)) {
    errors.paid_by_other = EXPENSE_MESSAGES.paidByOtherRequired;
  }
  if (data.reimbursed) {
    if (data.paid_by !== REIMBURSABLE_PAID_BY) {
      errors.reimbursed = EXPENSE_MESSAGES.reimbursedOnlyPepe;
    }
    if (isBlank(data.reimbursed_at)) {
      errors.reimbursed_at = EXPENSE_MESSAGES.reimbursedAtRequired;
    }
  }
  return errors;
}

/** Validates the mandatory cancellation reason; returns the trimmed reason or throws. */
export function requireCancelReason(reason: string | null | undefined): string {
  if (isBlank(reason)) throw new Error(EXPENSE_MESSAGES.cancelReasonRequired);
  return (reason as string).trim();
}
